import theme, { Image, Box, Heading, Flex, Text, Center, Spinner, Button, Divider, Link, AspectRatio } from 'src/theme'
import { LIST_ITEM_STATE, RedeemStep, RendererParameters } from '@dapperlabs/redeem.js'
import { getTrackPropertiesFromState, useRedemptionMachine } from '@dapperlabs/redeem.js'
import CampaignDetails from 'src/components/CampaignDetails'
import { CheckmarkEmpty, Warning } from '../Icons'
import { useCallback, useRef, useState } from 'react'
import { useAnalytics } from 'src/lib/analytics'
import { compileUrl } from '@dapperlabs/redeem.js'

const getIcon = (state: LIST_ITEM_STATE) => {
  switch (state) {
    case 'ACTIVE':
      return <Spinner />
    case 'IDLE':
      return <CheckmarkEmpty color={theme.colors.gray[200]} checkmarkColor={theme.colors.black} />
    case 'SUCCESS':
      return <CheckmarkEmpty color={theme.colors.green[500]} checkmarkColor={theme.colors.black} />
    case 'ERROR':
      return <Warning color={theme.colors.red[400]} w="24px" h="24px" />
  }
  return null
}

const CONTENT_MAP = {
  auth: 'Sign in with Dapper',
  auth_active: 'Signing in. If you do not see a pop up, you must enable them for this page in your browser.',
  auth_done: 'Signed in with Dapper',

  collection: 'Prepare account for NFT',
  collection_checking: 'Checking if account is ready',
  collection_preparing: (
    <>
      <Text>Please approve the transaction.</Text>
      <Text>{`If you aren't seeing the window to approve the transaction you may have popups blocked. Please check your browser settings.`}</Text>
    </>
  ),
  collection_waiting: 'Waiting for transaction to seal',
  collection_popup_blocked: 'Your account needs to be set up to receive the NFT, this is a one-time transaction per NFT collection.',
  collection_already: 'Account already prepared',
  collection_done: 'Account prepared',

  redeem: 'Redeem Code',
  redeem_done: 'Success!',
  redeem_redeeming: 'Redeeming your code',
  redeem_transferring: 'Transferring NFT to your account',
  
  // # Known error messages
  // ## auth
  error_AUTH_FAILED: 'There was a problem signing in.',

  // ## collection
  error_PREPARE_FAILED: 'There was a problem preparing your account.',
  error_CHECK_COLLECTION_FAILED: 'Unable to check that your account is ready.',
  
  // ## redeem
  error_USER_ALREADY_REDEEMED_CODE: 'You have already redeemed this code.',
  error_USER_ALREADY_REDEEMED_CAMPAIGN: 'You have already redeemed the maximum allowed codes for this campaign.',
  error_MAXIMUM_REDEMPTIONS_REACHED: 'This code has been used the maximum allowed times.',
  error_OTHER_ALREADY_REDEEMED_CODE: 'This code has already been redeemed.',
  error_CAMPAIGN_NOT_ACTIVE: 'This campaign is no longer active.',
  error_USER_CANT_RECEIVE: 'Your account is unable to receive this NFT.',

  error_UNKNOWN: 'An unknown error occurred.',
}

const MyItem = ({ state, messageKey, secondaryMessageKey, error }: RendererParameters) => {
  const [_, send] = useRedemptionMachine()
  let retryButton = null
  if (error && !error?.isTerminal) {
    if (secondaryMessageKey === 'collection_popup_blocked') {
      retryButton = (
        <Button
          mt={2}
          onClick={async () => send('FORCE_POPUP')}
          colorScheme='purple'
        >
          {'Prepare Account'}
        </Button>
      )
    } else {
      retryButton = (
        <Button
          mt={2}
          onClick={() => send('RETRY')}
          colorScheme='purple'
        >
          {'Try again'}
        </Button>
      )
    }
  }

  return (
    <Flex
      maxW='sm'
      borderWidth='1px'
      borderRadius='lg'
      overflow='hidden'
      bg="#212127"
      boxShadow='sm'
      mb={2}
      flexDirection='column'
    >
      <Flex p={2}>
        <Center w="32px" h="32px">
          {getIcon(state)}
        </Center>
        <Flex
          flex='1'
          flexDirection='column'
          justifyContent='center'
          pl={2}
        >
          {CONTENT_MAP[messageKey]}
        </Flex>
      </Flex>
      {(secondaryMessageKey) && (
        <Box
          borderTopWidth='1px'
          borderTopStyle="solid"
          p={2}
          bg="#101012"
        >
          <Text
            as='div'
            borderLeftWidth='4px'
            borderLeftStyle='solid'
            borderLeftColor={({
              [LIST_ITEM_STATE.IDLE]: 'gray.200',
              [LIST_ITEM_STATE.ACTIVE]: 'gray.200',
              [LIST_ITEM_STATE.SUCCESS]: 'green.500',
              [LIST_ITEM_STATE.ERROR]: 'red.400',
            })[state]}
            pl={2}
            fontSize='small'
          >
            {CONTENT_MAP[secondaryMessageKey]}
            {retryButton}
          </Text>
        </Box>
      )}
    </Flex>
  );
}

const CampaignMedia = ({ url }:{ url?: string;}) => {
  const vidRef = useRef<HTMLVideoElement>()
  const [isPlaying, setIsPlaying] = useState(false);
  const lowercased = url?.toLocaleLowerCase();
  switch (true) {
    case lowercased?.includes('.mp4'):
      return (
        <Box pos={'relative'}>
          {!isPlaying && (
            <Center
              pos='absolute'
              zIndex={1}
              w='100%'
              h='100%'
              onClick={() => {
                vidRef.current.play()
                setIsPlaying(true);
              }}
              cursor='pointer'
              opacity={0.75}
              _hover={{
                opacity: 1
              }}
            >
              <svg
                width="50"
                height="50"
                version="1.1"
                viewBox="0 0 1200 1200"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="white"
                  d="m600 0c-159.13 0-311.74 63.215-424.27 175.73-112.52 112.52-175.73 265.14-175.73 424.27s63.215 311.74 175.73 424.27c112.52 112.52 265.14 175.73 424.27 175.73s311.74-63.215 424.27-175.73c112.52-112.52 175.73-265.14 175.73-424.27 0-105.32-27.723-208.79-80.383-300-52.664-91.211-128.41-166.95-219.62-219.62-91.211-52.66-194.68-80.383-300-80.383zm192.1 616.24-290.45 167.69c-5.8008 3.3477-12.945 3.3477-18.746 0-5.8047-3.3516-9.375-9.5391-9.3789-16.238v-335.38c0-4.4102 1.5547-8.6758 4.3867-12.055 2.832-3.3789 6.7656-5.6484 11.109-6.4141 4.3398-0.76562 8.8125 0.023438 12.629 2.2266l290.44 167.69h0.003906c5.7969 3.3516 9.3711 9.5391 9.3711 16.238s-3.5742 12.887-9.3711 16.238z"
                />
              </svg>
            </Center>
          )}
          <AspectRatio>
            <video height='100%' controls={isPlaying} width='100%' ref={vidRef}>
              <source src={url} type="video/mp4" />
            </video>
          </AspectRatio>
        </Box>
      )
    default:
      return <Image src={url} alt="" height={260} my={0} mx="auto" />
  }
}

export default function Loaded () {
  const { track } = useAnalytics()
  const [current] = useRedemptionMachine()

  const handleView = useCallback(() => {
    track('view_nft-click', getTrackPropertiesFromState(current))
    const replaceables = {
      address: current.context.user.addr,
      nftId: current.context.nftId,
    }
    const targetUrl = compileUrl(current.context.campaign.successUrl, replaceables);
    window.location.href = targetUrl
  }, [current, track])

  const handleSupport = useCallback(() => {
    track('support-click', getTrackPropertiesFromState(current))
    // page will open via href in new tab
  }, [current, track])

  if (!!current.context.campaign?.targetUrl) {
    const url = compileUrl(current.context.campaign.targetUrl, { code: current.context.code })
    return (
      <Box p={4}>
        <Text>{`Hang tight, we're redirecting you to: `}<Link fontSize={'sm'} href={url}>{url}</Link></Text>
      </Box>
    )
  }

  return (
    <>
      {current?.context?.campaign?.imageUrl && <CampaignMedia url={current.context.campaign.imageUrl} />}
      <Box p={4}>
        <Box pl={2} pt={4} pb={4}>
          <Heading size='md'>{current.context.campaign?.name}</Heading>
        </Box>
        {current.matches('IDLE') ? (
          <CampaignDetails />
        ) : (
          <>
            <RedeemStep render={MyItem} step='AUTH' />
            {current.context.campaign.redemptionTypes?.includes('NFT') && <RedeemStep render={MyItem} step='COLLECTION' />}
            <RedeemStep render={MyItem} step='REDEEM' />
            {current.matches('REDEEM.DONE') && (
              <Button
                colorScheme={'green'}
                onClick={handleView}
                mb={2}
              >
                {current.context.campaign.redemptionTypes?.includes('DapperCredit')
                  ? 'Continue!' 
                  : 'View your NFT!'
                }
              </Button>
            )}
            {(current.context.error && !!current.context.retryAttempts) && (
              <Box m={2} mt={4} >
                <Divider mb={2} />
                <Text fontSize="smaller">
                  If you continue to experience issues, please{' '}
                  <Link
                    href={process.env.NEXT_PUBLIC_SUPPORT_URL}
                    onClick={handleSupport}
                    isExternal
                  >
                    contact our support team
                  </Link>.
                </Text>
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  )
}
